import { Box, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import type { ReactElement } from 'react';
import type { DonutLegendItem } from '../formatters/buildDonutLegendItems';
import { CHART_FONT_FAMILY } from '../theme/muiChartSlotProps';

type DonutChartLegendProps = Readonly<{
  items: readonly DonutLegendItem[];
  layout?: 'dashboard' | 'details';
}>;

const LEGEND_FONT_SIZE = '0.6875rem';

const LEGEND_LINE_HEIGHT = 1.25;

const legendTextBaseSx = {
  fontFamily: CHART_FONT_FAMILY,
  lineHeight: LEGEND_LINE_HEIGHT,
  color: 'text.primary',
  overflow: 'visible',
  textOverflow: 'clip',
  whiteSpace: 'normal',
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
} satisfies SxProps<Theme>;

const legendTextLayoutSx = {
  dashboard: {
    flex: '0 0 auto',
  },
  details: {
    minWidth: 'max-content',
    flex: '0 1 auto',
  },
} satisfies Record<'dashboard' | 'details', SxProps<Theme>>;

/** Name, share and count in columns (PT-6804): the numbers line up across the rows. */
const legendListBaseSx = {
  listStyle: 'none',
  m: 0,
  p: 0,
  display: 'grid',
  gridTemplateColumns: '8px minmax(0, max-content) auto auto',
  columnGap: 1.5,
  rowGap: 0.75,
  alignContent: 'center',
  alignItems: 'center',
  overflowY: 'auto',
} satisfies SxProps<Theme>;

const legendRowSx = {
  display: 'grid',
  gridTemplateColumns: 'subgrid',
  gridColumn: '1 / -1',
  alignItems: 'center',
  fontFamily: CHART_FONT_FAMILY,
  lineHeight: LEGEND_LINE_HEIGHT,
} satisfies SxProps<Theme>;

const legendNumberSx = {
  fontFamily: CHART_FONT_FAMILY,
  fontSize: LEGEND_FONT_SIZE,
  lineHeight: LEGEND_LINE_HEIGHT,
  color: 'text.secondary',
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap',
} satisfies SxProps<Theme>;

const legendListLayoutSx = {
  dashboard: {
    flex: '0 0 auto',
    width: 'max-content',
    maxWidth: '100%',
  },
  details: {
    flex: '0 1 auto',
    width: 'max-content',
    maxWidth: '100%',
    overflowX: 'visible',
  },
} satisfies Record<'dashboard' | 'details', SxProps<Theme>>;

export function DonutChartLegend({
  items,
  layout = 'dashboard',
}: DonutChartLegendProps): ReactElement {
  return (
    <Box
      component='ul'
      sx={{ ...legendListBaseSx, ...legendListLayoutSx[layout] }}
    >
      {items.map((item) => (
        <Box
          component='li'
          key={`${item.label}-${item.count}-${item.percentLabel}`}
          sx={legendRowSx}
        >
          <Box
            aria-hidden
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: item.color,
            }}
          />
          <Typography
            component='span'
            sx={{
              ...legendTextBaseSx,
              ...legendTextLayoutSx[layout],
              fontSize: LEGEND_FONT_SIZE,
              fontWeight: 500,
            }}
          >
            {item.label}
          </Typography>
          <Typography component='span' sx={legendNumberSx}>
            {item.percentLabel}
          </Typography>
          <Typography component='span' sx={legendNumberSx}>
            {item.count}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}
